import {
  useEffect, useMemo, useRef, useState,
  type PointerEvent as PekerHending, type ReactNode,
} from 'react'
import {
  penHistory,
  type FishGroup, type FishMove, type HistoryDay, type PenHistory,
  type VarselUke,
} from '../api'
import { isoAar, isoUke, nb } from './Varsel'
import { useT } from '../lang'

/* SYKLUSEN — the weeks behind, this week, and the weeks ahead, as one band.
 *
 * WHY THIS EXISTS, AND WHY IT IS NOT A CHART.
 *
 * Every dashboard this industry has been shown answers one small question:
 * what is the number today. A map, a table, a line graph — all three. But a
 * salmon farm does not experience itself as a number today. It experiences
 * itself as ONE ARC: smolt go in, they grow for sixteen to twenty months, lice
 * climb, you delouse, you harvest, the site lies fallow, and it starts again.
 * Every decision anybody makes on a walkway is a decision about where in that
 * arc they are standing and what is coming at them next.
 *
 * Nobody has an instrument for the arc. They have a spreadsheet per week.
 *
 * So this is the arc: time across, one lane per merd down. Read it left to
 * right and you are reading the generation. Read it top to bottom at one week
 * and you are reading the array. It is the same two axes the farm already
 * thinks in, and it is the pair no product has put on one surface — because
 * nobody else has the merd axis at all. The register counts localities and is
 * blind below them.
 *
 * FORWARD IS THE POINT, AND THE WEIGHTING SAYS SO.
 *
 * The first version of this ended at today, and that was backwards. Nothing on
 * a farm is decided by what merd 3 did in March. It is decided by whether merd
 * 3 will be over the limit in three weeks, whether the boat has to be booked
 * now, and whether the week it lands in is one of the tightened ones. History
 * is the evidence; the forecast is the decision.
 *
 * So the default view opens on the last few weeks and every week the model
 * will answer for, with NOW as a labelled line between them — and the whole
 * cycle is a button away rather than the thing you have to scroll past. Recent
 * weeks are there because a forecast with no run-up is a number without a
 * direction.
 *
 * ONE BAND, AND THE MERDER UNDER IT ONLY IF YOU ASK.
 *
 * The first version opened on seven lanes. On a farm where nobody has counted
 * per ring — which is most weeks, for most farms — those seven lanes carry one
 * number seven times, and the reader has to check all of them before they can
 * be sure it is not seven measurements that happen to agree. It is the same
 * mistake the map made when it printed 0,40 on every ring.
 *
 * So the default is ONE row: the figure the farm reported, which is the one
 * that goes to Mattilsynet. The merder open underneath it, and the reason to
 * open them is drawn on that row — a tick at the height of the worst single
 * ring on any week where a ring stood above the farm's own figure. A farm at
 * 0,45 is under the limit while one ring at 0,9 is not, and that is the week
 * somebody needs to look closer.
 *
 * POSITION AGAINST A RULE. NOT A LADDER OF COLOURS.
 *
 * Every week on this ribbon used to be a cell filled green, amber or red. That
 * is a three-step quantisation of the one number the whole product is about,
 * and it cost more than it looks: 0,26 and 0,49 are both amber, one of them is
 * a quiet week and the other is a boat that has to be booked. It also needed a
 * legend, and a legend is an admission that the colour is the only carrier —
 * the customer's verdict on the map's legend was that a product needing one is
 * too complex.
 *
 * So: one line per row, and the limit drawn through it as a rule. Height is
 * the figure, continuously. Being over is signs standing above a line, which
 * is a comparison nobody has to be taught, and the only red left is the area
 * between the line and the rule — so red says HOW MUCH over, at the size it
 * actually is, instead of being a category stamped on a week.
 *
 * THE RULE IS A STEP, AND THAT IS THE TIGHTENED WEEKS. Weeks 16–21 carry a
 * limit less than half the usual one, which is the single most Norwegian fact
 * in this domain and the one every generic tool renders as an unexplained
 * jump. A shaded column said "something is different here". A step in the rule
 * says what, and by how much, and this file still does not know what the
 * spring window is: it draws the limits it was handed, week by week.
 *
 * THE TWO HALVES ARE DRAWN AS DIFFERENT KINDS OF CLAIM. Left of the line is
 * what was counted: a line with a dot on every week somebody went out. Right
 * of it is what the model says will happen, drawn as p10–p90 opening out from
 * the last real measurement — because a forecast is an interval and drawing
 * its centre as a solid block reads as a measurement of a week that has not
 * happened. It has NO MERD AXIS and never will, so it appears on the farm's
 * row alone and never as seven per-ring predictions invented from one figure.
 * The playhead stops at now, because a map showing a week that has not
 * happened is a map of nothing.
 *
 * NO ALPHA. ANYWHERE. `docs/not-generic.md`: "Solid tokens, never alpha, so
 * every ratio is verifiable." The forecast band is the one shape that wants
 * transparency and does not get it; it is a solid token a step off the ground
 * it sits on, with the centre line drawn over it.
 *
 * THE FOUR THINGS THE HISTORY HALF SHOWS THAT A TABLE CANNOT.
 *
 * 1. "MERD 3 IS ALWAYS THE WORST" is a horizontal stripe. That sentence is a
 *    claim about the mouth of the fjord and how the current sets past it, and
 *    it is invisible in any single week. Here it is the shape of the lane.
 *
 * 2. AN EMPTY RING IS A HOLE IN THE RIBBON. Brakklegging is the most
 *    consequential period in the cycle and nothing ever draws it, because "no
 *    data" and "no fish" look identical in a table.
 *
 * 3. FISH MOVING BETWEEN MERDER IS A DIAGONAL. A per-ring series silently
 *    mixes two populations every time somebody shuffles a net, so "merd 3 got
 *    better" may only mean "different fish". Drawn across the lane boundary,
 *    the mixing is visible instead of silent.
 *
 * 4. THE TIGHTENED WEEKS ARE A STEP IN THE RULE, so the reader sees both that
 *    the limit moved and how far — rather than a series that appears to jump
 *    for no reason, which is what every generic tool draws.
 *
 * THE LIMIT IS NEVER COMPUTED HERE. It comes from the API — `grenseFor` for a
 * recorded week, `data.uken.details.grense` for a forecast one — exactly as
 * `Varsel` takes it. This file does not know that the tightened window is
 * weeks 16–21 and must not learn: it draws the limit each week came back with,
 * which is a statement about the data rather than a copy of a regulation that
 * has been amended twice.
 */

const LANE = 18         // one merd's plot, once the merder are open
const SPOR = 3
const GUTTER = 52       // the merd numbers
const AKSE = 22         // the week axis, and the month names on it
const GEN = 14          // the generation rule, hairline, along the top
/* The farm's own plot. The thing you actually read -- and it is read for
 * SLOPE, which means the height is not decoration.
 *
 * Cleveland's banking rule says the average slope in a series wants to be near
 * 45 degrees. Ljosoy's week-to-week change has a median of about 0,07, and
 * what that draws as depends on the ceiling, the height AND the width of a
 * column -- the last of which is set by how many weeks are in the window, and
 * was for a long time the term nobody was looking at:
 *
 *     54px, ceiling 1,00, 20 weeks (86px/uke)  ->  3,8px rise    ->   2,5 gr.
 *     84px, ceiling 0,75, 20 weeks (86px/uke)  ->  7,8px         ->   5,2 gr.
 *    108px, ceiling 0,60, 20 weeks (86px/uke)  -> 12,6px         ->   8,3 gr.
 *    148px, ceiling 0,60, 35 weeks (51px/uke)  -> 17,3px         ->  18,8 gr.
 *
 * The third row is where this got stuck: three rounds of work on the vertical
 * had moved the typical week from four degrees to eight, because the column
 * was eighty-six pixels wide and no height can outrun that. Widening the
 * window is what the last row does, and it is worth more than either of the
 * two changes above it -- see `naervindu`.
 *
 * Nineteen degrees is short of Cleveland's forty-five and that is the RIGHT
 * place to stop. Banking the MEDIAN to 45 banks the WOBBLE to 45 as well, and
 * the wobble is not the message. What matters is that the wobble and the
 * events stop looking alike: here the ordinary week is a 19-degree drift and
 * the jump from 0,10 to 0,47 in week 32 is a 60-degree wall. A figure that
 * makes noise look like news is worse than a flat one. */
const SUM = 148

/* The right-hand gutter: the latest figure on every row.
 *
 * Tufte's own fix for what a sparkline gives up. Shape without magnitude is
 * half a reading -- a line climbing steeply is alarming at 0,4 and irrelevant
 * at 0,04 -- and the cure is one number at the end of the row, where the eye
 * arrives anyway after reading left to right. One, not thirty: thirty is the
 * table this replaced. */
const TALL = 54

/* THE DELOUSING RAIL, directly above the band.
 *
 * A cycle is not a series of numbers, it is a series of numbers WITH
 * INTERVENTIONS IN IT. This one has nine of them. Without them the ribbon
 * draws 0,50 falling to 0,40 as weather -- a level that happened to drop --
 * when what actually happened is that a boat came and somebody deloused a
 * farm. Every fall on this band that matters has a cause, and the cause was a
 * decision somebody made.
 *
 * Its own lane rather than a mark inside the cell: the band is a measurement
 * and this is an act, and drawing an act on top of a measurement is how you
 * end up unable to read either. Ten pixels is enough for a tick and costs the
 * band nothing. */
const BEH = 10
const MINCELLE = 3

/** How many recorded weeks the near view keeps behind now.
 *
 *  A forecast with no run-up is a number with no direction — the eye reads
 *  "0,38 next week" as fine or as alarming depending entirely on whether the
 *  three weeks before it were 0,10 or 0,34. Twelve is a quarter, which is the
 *  span a delousing decision is actually argued over. */
const NAER = 12

/** And never more than this, however unbroken the run. Eighteen months is
 *  longer than any production cycle, so past it the window has stopped being
 *  "this cycle" and started being an archive -- which is what the whole-cycle
 *  toggle is for. */
const MAKSUKER = 78

interface Rad { pen_id: string; pen_no: string; retired: boolean }

/** One column. Either a week that happened or a week the model answers for,
 *  never both, and the flag is read everywhere rather than inferred from a
 *  date — a farm that has not counted for a fortnight has recorded weeks that
 *  are older than they look. */
interface Kol {
  key: string
  dato: string
  fram: boolean
  dag?: HistoryDay
  /** The other records filed in the same week — a recount after a delousing,
   *  or the register catching up with a figure the farm already typed. Drawn
   *  as their own dots on the same column; see `modell`. */
  flere?: HistoryDay[]
  /** A delousing anywhere in the week, unioned across every record in it. */
  behandlet?: boolean
  /** Forecast columns: the locality's predicted figure, the interval the
   *  model published around it, and the limit that week — all straight off the
   *  wire. `lo`/`hi` are p10 and p90; they are absent on the origin row and on
   *  any head that does not publish them, and the band is simply not drawn
   *  then rather than being invented from the centre. */
  varsel?: { v: number; lo: number | null; hi: number | null
             grense: number | null }
}

export function Syklus({
  siteId, grenseFor, moves, groups, varsel, valgt, onVelg,
}: {
  siteId: string
  /** The limit in force that week, from the API. Never derived here. */
  grenseFor: (iso: string) => number | null
  moves: FishMove[]
  groups: FishGroup[]
  /** The weeks ahead. Rows without `data.uken` are the origin observation
   *  rather than a forecast and are dropped. */
  varsel: VarselUke[]
  /** The day the map is showing. */
  valgt: string | null
  onVelg: (day: string) => void
}) {
  const t = useT()
  const boks = useRef<HTMLDivElement>(null)
  const [hist, setHist] = useState<PenHistory | null>(null)
  const [bredde, setBredde] = useState(0)
  const [peker, setPeker] = useState<number | null>(null)
  /** The whole cycle, rather than the weeks around now. Off by default: see
   *  the header. */
  const [alt, setAlt] = useState(false)
  /** The merder, under the farm's band. Closed by default: see the header. */
  const [merder, setMerder] = useState(false)
  const [apen, setApen] = useState(
    () => typeof window === 'undefined' || window.innerWidth >= 1000)

  const [feil, setFeil] = useState(false)

  useEffect(() => {
    let dead = false
    setHist(null)
    setFeil(false)
    void penHistory(siteId, 260)
      .then((r) => { if (!dead) setHist(r) })
      .catch(() => { if (!dead) setFeil(true) })
    return () => { dead = true }
  }, [siteId])

  /* WAITING IS A STATE, NOT AN ABSENCE.
     260 weeks is a real fetch, and until it landed this drew its header row
     and nothing else -- which does not read as "loading", it reads as "the
     band is gone". A surface that vanishes while it works teaches people it
     is unreliable, and they stop trusting it on the days it is right. */
  const laster = hist === null && !feil

  useEffect(() => {
    const el = boks.current
    if (!el) return
    const ro = new ResizeObserver(() => setBredde(el.clientWidth))
    ro.observe(el)
    setBredde(el.clientWidth)
    return () => ro.disconnect()
  }, [hist, apen])

  const modell = useMemo(() => {
    if (!hist || hist.days.length === 0) return null

    // Oldest first. The API answers newest-first because every other reader
    // wants "what happened lately"; an arc is read the way it was lived.
    const bak = [...hist.days].sort(
      (a, b) => a.counted_at.localeCompare(b.counted_at))

    /* ONE COLUMN PER ISO WEEK, AND THAT IS AN AXIS DECISION.
     *
     * A farm that recounts after a delousing files three records inside one
     * week — Ljøsøy has 17., 18. and 19. august — and one column each would
     * draw that week three times as wide as every other week on the ribbon.
     * On an axis whose whole job is slope, that is a lie about time: the last
     * week would appear to be a month and the run-up into it would be
     * compressed to match. The old week table had the same duplication and it
     * was the first thing the customer called out — "Uke 34" printed three
     * times, on three rows, as if it were three weeks.
     *
     * So the column is the WEEK, and the record drawn on it is the best one
     * that week: per-ring beats a single site figure beats the register,
     * newest first inside a tie. The others are not thrown away — they draw as
     * their own dots at the same x (see `flere`), which is what actually
     * happened: two counts, that week, that far apart. The exact daily rows
     * are in the table below, which is what the table is now for. */
    const RANG: Record<string, number> = { pens: 0, fish: 0, site: 1, register: 2 }
    const uker = new Map<string, HistoryDay[]>()
    for (const d of bak) {
      const key = ukeNøkkel(d.counted_at)
      const liste = uker.get(key)
      if (liste) liste.push(d)
      else uker.set(key, [d])
    }

    const kol: Kol[] = [...uker].map(([key, dager]) => {
      const rangert = [...dager].sort((a, b) =>
        (RANG[a.basis] ?? 3) - (RANG[b.basis] ?? 3)
        || b.counted_at.localeCompare(a.counted_at))
      const beste = rangert[0]!
      return {
        key,
        dato: beste.counted_at,
        fram: false,
        dag: beste,
        flere: rangert.slice(1),
        /* A boat either came that week or it did not, and no record
           contradicts another about it — so this is a union across the week
           and not a property of the row that won. */
        behandlet: dager.some((d) => d.treated),
      }
    })
    const brukt = new Set(kol.map((k) => k.key))

    /* The weeks ahead. Deduplicated against the recorded half by ISO WEEK and
       not by date, the same rule `_merge_days` enforces on the server: the
       model answers on a Monday and a crew counts on a Tuesday, and keyed by
       date the current week would appear twice with two different numbers and
       nothing saying which was which. The count wins; it happened. */
    for (const w of varsel ?? []) {
      const uken = w.data.uken
      if (!uken) continue
      const key = ukeNøkkel(w.time)
      if (brukt.has(key)) continue
      brukt.add(key)
      kol.push({
        key, dato: w.time.slice(0, 10), fram: true,
        varsel: { v: w.data.instant.details.voksneHunnlus,
                  lo: w.data.instant.details.p10 ?? null,
                  hi: w.data.instant.details.p90 ?? null,
                  grense: uken.details.grense },
      })
    }

    const naa = kol.filter((k) => !k.fram).length   // columns behind the line

    const rader: Rad[] = hist.pens.map((p) => ({
      pen_id: p.pen_id, pen_no: p.pen_no, retired: p.retired }))

    // The highest limit anywhere on the ribbon. Everything below it is a
    // tightened week and gets shaded — which is the spring window without this
    // file having to know what the spring window is.
    let tak: number | null = null
    for (const k of kol) {
      const g = k.fram ? (k.varsel?.grense ?? null) : grenseFor(k.dato)
      if (g !== null) tak = tak === null ? g : Math.max(tak, g)
    }

    return { kol, naa, rader, tak }
  }, [hist, varsel, grenseFor])

  /* THE WINDOW ACTUALLY DRAWN, AND WHY IT IS NO LONGER A FIXED TWELVE.
   *
   * Twelve recorded weeks plus eight forecast ones is twenty columns, and
   * twenty columns stretched across a desk monitor makes ONE WEEK EIGHTY-SIX
   * PIXELS WIDE while the whole vertical scale is a hundred and some. A figure
   * read for slope cannot survive that: the week-to-week change is drawn eight
   * times shorter than the gap between the weeks it happened over, so real
   * movement lies down flat and reads as a wobble. No amount of height fixes
   * it, because height is the numerator and this is the denominator.
   *
   * The answer is MORE WEEKS, NOT WIDER ONES. So the window is the current
   * unbroken run of weeks, and the run is cut only by how many weeks can be
   * drawn wide enough to still be a week you can point at. On Ljosoy that is
   * the whole 2026 block -- twenty-six register weeks back to February instead
   * of twelve back to June -- and the column halves.
   *
   * It stops at a HOLE, and that is the point of doing it this way rather than
   * simply asking for more weeks. The register's coverage for this locality is
   * blocks with years between them (2022, 2024, 2026), and columns are one per
   * week WITH DATA, so reaching past a hole would butt July 2024 against
   * February 2026 with nothing between them saying eighteen months passed.
   * Until the holes are drawn as holes, the window stops at the first one. */
  const kutt = modell && !alt ? nærvindu(modell, bredde, merder) : 0
  const kol = modell ? modell.kol.slice(kutt) : []
  const naa = modell ? modell.naa - kutt : 0
  const n = kol.length
  const indeks = useMemo(
    () => new Map(kol.map((k, i) => [k.key, i])), [kol])

  /* Which week is on screen, worked out before the fold so the header can name
     it whether the lanes are drawn or not. `peker` is the finger mid-drag and
     outranks the committed week; without it the readout lags the map by one
     pointer event and reads as a bug. */
  const valgtI = valgt !== null
    ? (indeks.get(ukeNøkkel(valgt)) ?? Math.max(0, naa - 1))
    : Math.max(0, naa - 1)
  const vist = peker !== null ? peker : valgtI
  const vistKol = kol[vist] ?? null
  const vistGrense = vistKol
    ? (vistKol.fram ? (vistKol.varsel?.grense ?? null)
      : grenseFor(vistKol.dato)) : null
  const vistVerdi = vistKol
    ? (vistKol.fram ? (vistKol.varsel?.v ?? null)
      : (vistKol.dag?.adult_female ?? null)) : null

  /* THE SENTENCE, AND IT LEADS.
     A ribbon that makes somebody read twenty columns before they know whether
     anything is wrong is an engineering read-out. The one thing a farmer wants
     off this surface is whether the weeks ahead cross the limit and when — so
     that is said first, in words, in the serif, and the ribbon underneath is
     the evidence for it. Computed from the model's own weeks and its own
     limits; nothing here decides what "over" means. */
  const varsel_ = (modell?.kol ?? []).filter((k) => k.fram && k.varsel)
  let topp: Kol | null = null
  let kryss: Kol | null = null
  let krysssIdx = -1
  /* An indexed for, not forEach: TypeScript stops narrowing a `let` that is
     only ever assigned inside a closure, and `topp` then reads as `never` at
     every use below. */
  for (let j = 0; j < varsel_.length; j++) {
    const k = varsel_[j]!
    if (!topp || k.varsel!.v > topp.varsel!.v) topp = k
    if (!kryss && k.varsel!.grense !== null
        && k.varsel!.v >= k.varsel!.grense) { kryss = k; krysssIdx = j }
  }
  /* HOW LONG, NOT WHICH COORDINATE.
     "Over grensen i uke 36" is a coordinate, and a coordinate has to be
     subtracted from today before it means anything. The persona this is built
     for is a farmer on a walkway deciding whether to book a boat, and the
     quantity that decision is actually made on is HOW MANY WEEKS ARE LEFT.
     The week number still gets printed, because that is what goes in a diary
     -- but second, and once. The old line said "uke 36" twice and the limit a
     third time.

     `varsel_` is the forecast weeks in order and one column is one ISO week,
     so the index IS the count and there is no date arithmetic to get wrong. */
  const ukerTil = krysssIdx >= 0 ? krysssIdx + 1 : 0

  /* ONE ROW, NOT FOUR.
     The band had a toggle row, a sentence, a playhead read-out and a footnote
     stacked above it, and on the map page those four rows of small text cost
     more height than the band itself — a strip about the farm eating a
     quarter of the picture of the farm. They are one line now: the answer on
     the left, where reading starts, the controls pushed to the right edge.
     The footnote is gone; the one fact it carried — that a week ahead of the
     line is the locality and not the pen — is said by the read-out at the
     moment the playhead is actually standing on such a week, which is the
     only moment it changes anything. */
  const tittel = (
    <div className="syklus-topp">
      <button type="button" className="syklus-bryter"
        aria-expanded={apen} onClick={() => setApen(!apen)}>
        {apen ? '▾' : '▸'}{' '}{t('The cycle')}
      </button>

      {/* THE ANSWER, BEFORE THE EVIDENCE.
          In the serif, because docs/not-generic.md puts Spectral on the things
          people read to decide — and this is the one line on the screen
          anybody decides anything from.

          THE RED IS A DOT, NOT THE SENTENCE. --alert is 3.20:1 on --surface:
          fine for a graphic, under the floor for text, which is exactly why
          --alert-text exists. But a pale salmon headline is the palest thing
          on the page and reads as decoration rather than as an alarm. So the
          text goes back to full contrast and the brand mark does the
          signalling — one solid #e63926 circle, the logo's own move: "the
          entire mark is one red circle … detected." */}
      {apen && topp && (
        <p className="syklus-svar">
          {kryss && <i className="syklus-prikk" aria-hidden="true" />}
          {kryss
            ? <>{t('Over the limit in')} {ukerTil}{' '}
              {ukerTil === 1 ? t('week') : t('weeks')}</>
            : <>{t('Under the limit all')} {varsel_.length}{' '}
              {t('weeks ahead')}</>}
          {/* The evidence, once each. The week number because that is what
              gets written down, the figure because that is what gets typed
              into a form. The limit is not repeated here: the playhead
              read-out on the right already prints it as "0,40 av 0,50", where
              the word "av" says what it is. */}
          <span className="faint">
            {' · '}{t('week')} {isoUke(topp.dato)}
            {', '}{t('at')} {nb(topp.varsel!.v)}
          </span>
        </p>
      )}

      {/* THE ALIVE INDICATOR. Not a second job for the dot and not a fourth
          place for the red — it is the FIRST of the three in
          docs/principles.md, "the alive indicator while data is arriving".
          The same 8px circle that says `detected` says `arriving`; only the
          motion tells them apart. */}
      {apen && laster && (
        <span className="syklus-peker faint">
          <i className="syklus-prikk lever" aria-hidden="true" />
          {t('Fetching the cycle')}
        </span>
      )}

      <span className="syklus-fyll" />

      {/* Where the playhead is. On the same row and to the right, because it
          changes under the finger and the sentence must not move when it
          does. */}
      {apen && vistKol && (
        <span className="syklus-peker faint">
          {t('Week')} {isoUke(vistKol.dato)}
          {vistVerdi !== null && <>{' · '}<strong>{nb(vistVerdi)}</strong></>}
          {vistGrense !== null && <>{' '}{t('of')} {nb(vistGrense)}</>}
          {modell?.tak != null && vistGrense !== null
            && vistGrense < modell.tak
            && <>{' · '}<em>{t('tightened')}</em></>}
          {vistKol.behandlet && <>{' · '}<em>{t('delousing')}</em></>}
          {vistKol.fram && <>{' · '}{t('the locality, not the pen')}</>}
        </span>
      )}

      {apen && modell && (
        <>
          <button type="button" className="syklus-bryter omfang"
            aria-expanded={merder} onClick={() => setMerder(!merder)}>
            {merder ? t('Hide the pens')
              : `${modell.rader.length} ${t('pens')}`}
          </button>
          <button type="button" className="syklus-bryter"
            onClick={() => setAlt(!alt)}>
            {alt ? t('Around now') : t('The whole cycle')}
          </button>
        </>
      )}
    </div>
  )

  if (!apen || !modell || bredde === 0 || n === 0) {
    /* Held at the height the band will occupy, so nothing under it jumps when
       the weeks land. A skeleton that is the wrong size is a second layout. */
    const tomt = !laster && !feil && (modell === null || n === 0)
    return (
      <div className="syklus" ref={boks}>
        {tittel}
        {apen && feil && (
          <p className="syklus-tom">{t('The cycle could not be fetched.')}</p>
        )}
        {apen && tomt && (
          <p className="syklus-tom">{t('No weeks on record for this farm yet.')}</p>
        )}
        {apen && !feil && !tomt && (
          <div className="syklus-skjelett" role="status" aria-live="polite"
            aria-label={t('Fetching the cycle')} />
        )}
      </div>
    )
  }

  const { rader } = modell

  const gutterN = merder ? GUTTER : 8
  const cw = Math.max(MINCELLE, (bredde - gutterN - TALL - 6) / n)
  const lanesH = merder ? rader.length * (LANE + SPOR) + 4 : 0
  const H = GEN + BEH + SUM + lanesH + AKSE
  const W = gutterN + cw * n + TALL
  const luft = cw > 10 ? 1.5 : 0.5

  const x = (i: number) => gutterN + i * cw
  const xm = (i: number) => gutterN + i * cw + cw / 2
  const y = (r: number) => GEN + BEH + SUM + 4 + r * (LANE + SPOR)
  const lane = new Map(rader.map((r, i) => [r.pen_id, i]))

  /* ------------------------------------------------------------ the scale

     ONE SCALE FOR THE WHOLE FIGURE, and it is computed from everything the
     figure could draw rather than from what it is drawing right now.

     `WelfareScale.tsx`: "Same geometry every time you look, so the eye learns
     where 135 is." Two consequences, both load-bearing:

       Every lane shares the farm's scale. A per-lane scale would make two
          identical-looking lines mean different numbers, which is the exact
          failure a small-multiple exists to avoid — the whole reason to stack
          seven of these instead of drawing seven charts.

       Opening the merder does not rescale. The per-ring figures are in the
          maximum whether or not their lanes are drawn, so the farm's band sits
          at the same height before and after the fold. A band that jumps when
          you open a drawer teaches people the drawer changed the data.

     The limits are in the maximum too, so a farm that has never been near its
     limit still draws the limit inside the frame — which is the comparison the
     whole ribbon exists to support. */
  const grense = kol.map((k) => (k.fram ? (k.varsel?.grense ?? null)
    : grenseFor(k.dato)))

  /* THE SCALE IS SET BY WHAT IS DRAWN AND BY WHAT IS DECIDED ON.
   *
   * It used to be set by everything the model had ever heard of, and the band
   * was unreadable because of it. Ljosoy: the farm's own series runs 0,02 to
   * 0,47, the limit is 0,50 and the forecast peaks at 0,53 -- so the picture
   * wants a ceiling around three quarters. It got 1,00, from two places:
   *
   *   A RING NOBODY IS LOOKING AT. Every pen's figure entered the scale even
   *      with the lanes shut, and one ring at 0,90 squashed the farm's line to
   *      make room for seven rows that were not being drawn. A shared scale is
   *      right ACROSS ROWS THAT ARE ON SCREEN TOGETHER -- WelfareScale, "same
   *      geometry every time you look" -- and it is simply wrong to reserve
   *      height for a row nobody can see.
   *
   *   THE FORECAST'S UPPER TAIL. p90 is an uncertainty bound, not an
   *      observation, and letting it set the ceiling means THE LESS SURE THE
   *      MODEL IS, THE FLATTER THE RECORD OF WHAT ACTUALLY HAPPENED. That is
   *      backwards. The interval is still drawn in full; where it runs past
   *      the top it clips, and an interval touching the roof reads as "this
   *      could go well over", which is exactly what a wide p90 means.
   *
   * The result at Ljosoy is a typical week's movement going from eight pixels
   * to eighteen. Cleveland's banking-to-45 is not a nicety on a series read
   * for slope; it is the difference between a shape and a flat line. */
  /* THE LADDER, AND WHY IT IS FINE NEAR THE BOTTOM.
     A ladder of 0,2 / 0,3 / 0,5 / 0,75 / 1 puts a farm whose limit is 0,50
     under a ceiling of 0,75 -- half as much band again as the rule it is
     measured against, given away to a region no week is allowed to reach. The
     rungs are close together where farms actually live, and they only spread
     out above 1,0 where the difference between 3 and 5 is academic. */
  const TRINN = [0.1, 0.15, 0.2, 0.25, 0.3, 0.35, 0.4, 0.5, 0.6, 0.75,
                 0.9, 1, 1.25, 1.5, 2, 3, 5, 8, 12, 20]
  let topp2 = 0
  kol.forEach((k, i) => {
    const g = grense[i] ?? null
    if (g !== null) topp2 = Math.max(topp2, g)
    if (k.fram) {
      topp2 = Math.max(topp2, k.varsel?.v ?? 0)
      return
    }
    for (const d of [k.dag, ...(k.flere ?? [])]) {
      if (!d) continue
      if (d.adult_female !== null) topp2 = Math.max(topp2, d.adult_female)
      /* Only once the lanes are open, and then the scale genuinely is shared:
         the point of opening them is to see a ring standing above the farm's
         mean, and that comparison is only true if both are measured against
         the same ceiling. The band reflows when they open, which is a change
         the reader made happen and can undo. */
      if (!merder) continue
      for (const c of Object.values(d.cells)) {
        if (c.adult_female !== null) topp2 = Math.max(topp2, c.adult_female)
      }
    }
  })
  /* Eight per cent of headroom, and no more. A series that touches its own
     maximum draws that week flat against the ceiling and loses the peak it is
     the whole point of -- but every percent above that is band spent on a
     region the data never visits. With the rungs above this close together,
     eight is enough to lift the peak off the roof and nothing is wasted. */
  const skala = TRINN.find((s) => s >= topp2 * 1.08) ?? Math.max(0.5, topp2 * 1.08)

  /** A value, in the row that starts at `top` and is `h` tall. Clamped, so a
   *  figure past the ceiling draws at the ceiling rather than outside its own
   *  row and over the row above it. */
  const yv = (v: number, top: number, h: number) =>
    top + h - Math.max(0, Math.min(1, v / skala)) * h

  /* ---------------------------------------------------------- the drawing

     WHAT REPLACED THE COLOUR LADDER, AND WHY.

     Every cell on this ribbon used to be filled green, amber or red by
     `antallFor` — under half the limit, half to whole, at or over. Three
     things were wrong with that, and they compounded:

       IT QUANTISED THE ONE NUMBER THAT MATTERS. 0,26 and 0,49 are both amber.
          One of them is a quiet week and the other is a boat that has to be
          booked, and the drawing could not tell them apart. Position against
          the limit separates them continuously and for free.

       IT NEEDED A LEGEND, and a legend is an admission that the colour is the
          only carrier. The rule this replaced it with is drawn on the figure
          and labelled with its own number, so there is nothing left to look
          up.

       IT SPENT THE RED. The palette has red in a small number of places on
          purpose, and painting every over-limit week as a solid red block used
          it as a category rather than as an alarm.

     So: one line per row, one rule through it, and the ONLY red is the area
     between the two where the line is above. Red now says how much over rather
     than merely that, and a week one louse past the limit looks like a week one
     louse past the limit.

     THE RULE IS A STEP, NOT A STRAIGHT LINE, and that is the whole of the
     tightened-weeks feature. Weeks 16-21 carry a limit less than half the
     usual one; the ribbon used to shade those columns and hope the reader
     wondered why. Drawn as a step in the rule, the tightening is visible AND
     quantified, and this file still does not know what the spring window is —
     it draws the limits it was handed, week by week. */
  const marker: ReactNode[] = []

  /** The limit, as it stood each week, stepped across a row. */
  const grensesti = (top: number, h: number): string => {
    let d = ''
    let forrige: number | null = null
    for (let i = 0; i < n; i++) {
      const g = grense[i] ?? null
      if (g === null) { forrige = null; continue }
      const gy = yv(g, top, h)
      if (forrige === null) d += `M${x(i)},${gy}`
      else if (forrige !== gy) d += `L${x(i)},${gy}`
      d += `L${x(i) + cw},${gy}`
      forrige = gy
    }
    return d
  }

  /** The series, broken wherever there is no figure.
   *
   *  `Doegnstripe`: "hullet mellom dem ER stoppen". A fortnight nobody counted
   *  in is a gap in the line and not a straight run between the two weeks that
   *  bracket it — joining across it would draw a measurement that was never
   *  taken, at every pixel in between. */
  /** The same series, closed down to the floor: the level as a SILHOUETTE
   *  rather than as a wire.
   *
   *  A two-pixel hairline stretched across most of two thousand pixels is a
   *  thread, and a thread has no body -- the eye has to trace it to read it,
   *  which is the opposite of what a glance is. Filled, the same numbers read
   *  as a skyline against the rule above them, and "how much room is left" is
   *  a distance you see rather than one you measure.
   *
   *  WHAT THE FILL IS ALLOWED TO CLAIM, because `.syk-over` argues the other
   *  way about the red and both arguments have to hold at once. The excess
   *  over the limit is drawn per WEEK, as a column, precisely because the
   *  space between two counts is an interpolation and shading it red would
   *  assert a breach on days nobody was out. This fill is bounded above by the
   *  path the LINE ALREADY DRAWS. It asserts exactly what the line asserts and
   *  not one thing more; a breach is a claim, and a level is not.
   *
   *  Only the farm's own band, and only the half that happened. Seven filled
   *  lanes would be a stack of solids, and a filled forecast would give a
   *  prediction the visual weight of a measurement. */
  const flatesti = (top: number, h: number, vs: (number | null)[]): string => {
    const gulv = top + h
    let d = ''
    let start = -1
    const lukk = (slutt: number) => {
      if (start < 0 || slutt < start) return
      d += `L${xm(slutt)},${gulv}L${xm(start)},${gulv}Z`
    }
    for (let i = 0; i < n; i++) {
      const v = vs[i] ?? null
      if (v === null || v === undefined) {
        if (start >= 0) lukk(i - 1)
        start = -1
        continue
      }
      d += `${start < 0 ? 'M' : 'L'}${xm(i)},${yv(v, top, h)}`
      if (start < 0) start = i
    }
    if (start >= 0) lukk(n - 1)
    return d
  }

  const verdisti = (top: number, h: number, vs: (number | null)[]): string => {
    let d = ''
    let apen = false
    for (let i = 0; i < n; i++) {
      const v = vs[i] ?? null
      if (v === null || v === undefined) { apen = false; continue }
      d += `${apen ? 'L' : 'M'}${xm(i)},${yv(v, top, h)}`
      apen = true
    }
    return d
  }

  /** How much this week stood over the limit, as a bar rising off the rule.
   *
   *  Per week and not as a filled area between two curves: a week is a
   *  measurement and the space between two of them is an interpolation, so an
   *  area would shade an excess on days nobody went out. The bar stands on the
   *  week it was measured in and nowhere else. */
  const overskudd = (
    /* AT OR OVER, NOT OVER. `antallFor` is the API's rule and it reads
       `verdi >= grense` — a farm exactly at 0,50 against a 0,50 limit is in
       breach, and it is the single easiest thing in this file to get wrong by
       one character. Every comparison against a limit on this ribbon is >=. */
    nøkkel: string, top: number, h: number, vs: (number | null)[],
  ) => {
    for (let i = 0; i < n; i++) {
      const v = vs[i] ?? null, g = grense[i] ?? null
      if (v === null || v === undefined || g === null || v < g) continue
      const gy = yv(g, top, h), vy = yv(v, top, h)
      marker.push(<rect key={`o${nøkkel}${i}`} className="syk-over"
        x={x(i) + luft / 2} y={vy} width={Math.max(1, cw - luft)}
        height={Math.max(1, gy - vy)} />)
    }
  }

  /* ------------------------------------------------------------- the farm */

  const SY = GEN + BEH
  const sitev = kol.map((k) => (k.fram ? (k.varsel?.v ?? null)
    : (k.dag?.adult_female ?? null)))

  /* THE FORECAST'S INTERVAL, drawn as the interval it is. The model publishes
     p10 and p90 and the ribbon used to throw both away and draw the centre as
     a solid block — which reads as a measurement of a week that has not
     happened. The band opening out to the right of the line is the one honest
     difference between the two halves of this figure, and it costs no
     vocabulary: everybody has seen a forecast widen. */
  const fram = kol.map((k, i) => ({ k, i })).filter(({ k }) => k.fram && k.varsel)
  if (fram.length > 0) {
    const har = fram.some(({ k }) => k.varsel!.lo !== null && k.varsel!.hi !== null)
    if (har) {
      const opp = fram.map(({ k, i }) =>
        `${xm(i)},${yv(k.varsel!.hi ?? k.varsel!.v, SY, SUM)}`)
      const ned = [...fram].reverse().map(({ k, i }) =>
        `${xm(i)},${yv(k.varsel!.lo ?? k.varsel!.v, SY, SUM)}`)
      /* Anchored on the last week that happened, so the band grows out of the
         record rather than appearing beside it with a seam. */
      const anker = naa > 0 ? (sitev[naa - 1] ?? null) : null
      const feste = anker !== null
        ? `${xm(naa - 1)},${yv(anker, SY, SUM)} ` : ''
      marker.push(<polygon key="framband" className="syk-framband"
        points={`${feste}${opp.join(' ')} ${ned.join(' ')}`} />)
    }
  }

  /* The silhouette goes down FIRST, so the rule, the excess and the line all
     sit on top of it rather than being swallowed by it. */
  marker.push(<path key="sumflate" className="syk-flate"
    d={flatesti(SY, SUM, sitev.map((v, i) => (kol[i]!.fram ? null : v)))} />)

  marker.push(<path key="sumgrense" className="syk-grense"
    d={grensesti(SY, SUM)} />)

  /* THE RULE SAYS WHAT IT IS.
     A dashed horizontal line across a chart is a convention, and a convention
     is something you have to have been taught. The figure already prints the
     rule's VALUE on its right-hand end; one word on the left-hand end is what
     turns that number from a stray decimal into a limit. Faint, because it is
     said once and then known -- the reader who needs it reads it on the first
     visit and never again. */
  {
    const g0 = grense[0] ?? null
    if (g0 !== null && cw * n > 180) {
      marker.push(<text key="grenseord" className="syk-grenseord"
        x={gutterN + 3} y={yv(g0, SY, SUM) - 5}>
        {t('the limit')} <tspan className="tall">{nb(g0)}</tspan>
      </text>)
    }
  }
  overskudd('sum', SY, SUM, sitev)
  /* `sum` marks the farm's own band. The lanes below draw with the same two
     classes and must stay a hair: seven three-pixel strokes in --text under
     each other is a stack of equals, and the farm's line is not their equal --
     it is the number the rule is written against. */
  marker.push(<path key="sumlinje" className="syk-linje bak sum"
    d={verdisti(SY, SUM, sitev.map((v, i) => (kol[i]!.fram ? null : v)))} />)
  marker.push(<path key="sumfram" className="syk-linje fram sum"
    d={verdisti(SY, SUM, sitev.map((v, i) => (
      kol[i]!.fram || i === naa - 1 ? v : null)))} />)

  kol.forEach((k, i) => {
    const v = sitev[i] ?? null
    if (v === null) return
    const g = grense[i] ?? null
    const tt = `${t('Week')} ${isoUke(k.dato)} · ${nb(v)}`
      + (g !== null ? ` ${t('of')} ${nb(g)}` : '')
      + (k.fram ? ` · ${t('forecast')}` : '')
    marker.push(<circle key={`sp${i}`}
      className={`syk-punkt${k.fram ? ' fram' : ''}${
        g !== null && v >= g ? ' over' : ''}`}
      cx={xm(i)} cy={yv(v, SY, SUM)} r={cw > 7 ? 2.4 : 1.5}>
      <title>{tt}</title>
    </circle>)

    /* A DELOUSING THAT WEEK, on its own rail above the plot. The band is a
       measurement and this is an act; drawing an act on top of a measurement
       is how you end up unable to read either. */
    if (!k.fram && k.behandlet) {
      const cx = xm(i)
      const w = Math.min(7, Math.max(3, cw)) / 2
      marker.push(<polygon key={`beh${i}`} className="syk-beh"
        points={`${cx - w},${GEN + 1} ${cx + w},${GEN + 1} ${cx},${GEN + BEH - 1}`}>
        <title>{`${t('Week')} ${isoUke(k.dato)} · ${t('delousing')}`}</title>
      </polygon>)
    }

    /* THE OTHER COUNTS THAT WEEK. A farm that recounts after a delousing
       files two or three records inside one week, and the fall between them is
       the most informative thing on the ribbon — it is the treatment working,
       measured. They sit on the same column because they are the same week,
       unjoined because a line between two counts three days apart at this
       scale would be a stroke pretending to be a trend. */
    for (const annen of k.flere ?? []) {
      if (annen.adult_female === null) continue
      marker.push(<circle key={`sa${i}${annen.counted_at}`}
        className={`syk-punkt annen${
          g !== null && annen.adult_female >= g ? ' over' : ''}`}
        cx={xm(i)} cy={yv(annen.adult_female, SY, SUM)} r={cw > 7 ? 1.6 : 1.2}>
        <title>{`${annen.counted_at} · ${nb(annen.adult_female)}`}</title>
      </circle>)
    }

    /* THE REASON TO OPEN THE MERDER, drawn where the reason actually is.
       A farm at 0,45 is under the limit while one of its rings at 0,9 is not,
       and the reported figure says nothing about that. A tick at the worst
       ring's own height says it in the row's own units: this week is not as
       flat as this line. Only while the lanes are shut — once they are open
       the lane draws it properly and a second mark for it is noise. */
    if (merder || k.fram) return
    let verst: number | null = null
    for (const c of Object.values(k.dag?.cells ?? {})) {
      if (c.adult_female === null) continue
      verst = verst === null ? c.adult_female : Math.max(verst, c.adult_female)
    }
    if (verst === null || verst <= v) return
    const ty = yv(verst, SY, SUM)
    const halv = Math.min(4, cw / 2.4)
    marker.push(<line key={`verst${i}`} className="syk-verst"
      x1={xm(i) - halv} x2={xm(i) + halv} y1={ty} y2={ty}>
      <title>{`${t('worst pen this week')} · ${nb(verst)}`}</title>
    </line>)

    /* OFF THE TOP, SAID OUT LOUD.
       The scale is no longer stretched to fit a ring the reader cannot see
       (see the comment on `topp2`), so a ring above the ceiling now clamps to
       it -- and a mark sitting silently on the roof is the one thing worse
       than a squashed band, because it reads as "at the ceiling" rather than
       "past it". So when it clamps it says its own figure. Ljosoy's 0,90 ring
       against a 0,75 ceiling is a louder statement printed on the roof than it
       ever was as a tick two thirds of the way up a flattened band.

       Only where a column is wide enough to hold four characters without
       reaching into the next week. */
    if (verst > skala && cw > 26) {
      marker.push(<text key={`vt${i}`} className="syk-verstall"
        x={xm(i) + halv + 3} y={ty + 3}>{nb(verst)}</text>)
    }
  })

  /* THE RAIL SAYS WHAT ITS MARKS ARE.
     Nine triangles above a line is a glyph, and a glyph is something you have
     to have been told. The band already names the other rail -- "grensen" on
     the limit -- and this is the same word's worth of ink for the same gain:
     the reader learns it once, on the first visit, and never reads it again.
     It is also the half of the figure that is about DECISIONS rather than
     measurements, which is the half a farmer is actually in.

     Only where the first triangle is far enough right to leave room, since a
     word underneath one would be worse than no word at all. */
  {
    let først = -1
    for (let i = 0; i < n; i++) {
      if (!kol[i]!.fram && kol[i]!.behandlet) { først = i; break }
    }
    if (først >= 0 && x(først) > gutterN + 62) {
      marker.push(<text key="behord" className="syk-behord"
        x={gutterN + 3} y={GEN + BEH - 2}>{t('delousing')}</text>)
    }
  }

  /* THE WORST WEEK ON THE RECORD SAYS ITS OWN NUMBER, WHERE IT HAPPENED.
     Beautiful Evidence: a sparkline carries no axis, and pays for it by
     printing the extremes in place. Until now this figure printed no value at
     all on the plot -- the only numbers were in the header, which names the
     week under the finger, and in the right-hand gutter, which names the last
     one. So the tallest thing on the ribbon, the week the whole shape is
     about, was the one week you had to hover to read.

     The maximum only. The minimum on a lice series is nearly always 0,00 or
     close to it, and a farm that counted nothing is not a finding. */
  {
    let toppI = -1, toppV = -Infinity
    for (let i = 0; i < n; i++) {
      if (kol[i]!.fram) continue
      const v = sitev[i]
      if (v === null || v === undefined || v <= toppV) continue
      toppV = v; toppI = i
    }
    /* Not when the peak IS the last week: the gutter already prints that
       number, an arm's length to the right, and two copies of one figure on
       one row is the kind of redundancy this band was built to remove. */
    if (toppI >= 0 && toppI !== naa - 1 && cw > 18) {
      const py = yv(toppV, SY, SUM)
      /* Above the point, unless the point is too near the roof to hold a line
         of text -- then below it, where there is always room, because the
         scale is chosen so nothing sits on the floor. */
      const over = py - SY > 18
      marker.push(<text key="topptall" className="syk-topptall"
        x={xm(toppI)} y={over ? py - 9 : py + 18}>{nb(toppV)}</text>)
    }
  }

  /* ------------------------------------------------------------ the merder

     A REGISTER WEEK IS DRAWN ONCE, ON THE FARM'S ROW.

     Every lane used to fall back to the farm's own figure when nobody had
     counted that ring — so a register week, which has no merd axis and never
     will, printed one number across all seven lanes. Eight marks carrying one
     fact, and a reader had to check all eight before they could be sure it was
     not seven measurements that happen to agree. It is the same mistake the
     map made when it printed 0,40 on every ring, and the customer's verdict on
     that one was "that is too complex, noone will understand".

     So a lane draws what was measured IN THAT RING and nothing else. The gap
     is the fact: nobody counted here. The farm's line above it is unbroken
     through the same weeks, which is where the locality figure lives and the
     only place it is true. */
  if (merder) {
    rader.forEach((rad, r) => {
      const top = y(r)
      marker.push(<path key={`g${rad.pen_id}`} className="syk-grense svak"
        d={grensesti(top, LANE)} />)

      const vs = kol.map((k) => {
        if (k.fram || !k.dag) return null
        return k.dag.cells[rad.pen_id]?.adult_female ?? null
      })
      overskudd(rad.pen_id, top, LANE, vs)
      marker.push(<path key={`l${rad.pen_id}`} className="syk-linje bak"
        d={verdisti(top, LANE, vs)} />)

      kol.forEach((k, i) => {
        const v = vs[i] ?? null
        if (v !== null) {
          const g = grense[i] ?? null
          marker.push(<circle key={`p${rad.pen_id}${i}`}
            className={`syk-punkt${g !== null && v >= g ? ' over' : ''}`}
            cx={xm(i)} cy={yv(v, top, LANE)} r={cw > 7 ? 1.9 : 1.3}>
            <title>{`${t('Pen')} ${rad.pen_no} · ${t('Week')} `
              + `${isoUke(k.dato)} · ${nb(v)}`}</title>
          </circle>)
          return
        }
        /* THE HOLE IN THE RIBBON. A ring with no fish in it draws a hairline
           on its own floor — not a colour and not a pale mark, because
           brakklegging is an absence and the eye reads an absence faster than
           any symbol for one. Distinct from an uncounted week, which draws
           nothing at all: "no fish" and "nobody went out" are different facts
           and a table has never been able to tell them apart. */
        if (!k.fram && (k.dag?.empty_pens ?? []).includes(rad.pen_id)) {
          marker.push(<line key={`t${rad.pen_id}${i}`} className="syk-tom"
            x1={x(i)} x2={x(i) + cw} y1={top + LANE} y2={top + LANE} />)
        }
      })
    })
  }

  /* --------------------------------------------------- the latest figure

     ONE NUMBER PER ROW, AT THE END OF IT, IN THE SERIF.

     A sparkline carries shape and loses magnitude, and the fix is the one
     Tufte gives it: print the current value at the right-hand end, where the
     eye already is after reading left to right. Thirty numbers on a row would
     be the table this replaced; one number is the row's own answer, and the
     rest are a tooltip away.

     The FIGURE it prints is the last one that happened -- never a forecast.
     A row whose headline number is a prediction is a row that reports a week
     nobody has been out in. */
  const tall: ReactNode[] = []

  /* THE LIMIT'S VALUE USED TO SIT HERE, AT THE RIGHT-HAND EDGE, and that was
   * the last thing in the figure reading as an AXIS. Two numbers stacked in
   * the right margin at two different heights is what a y-axis looks like,
   * whatever either of them actually means -- and this one meant the rule,
   * which is not a scale at all. It has gone to the left end of the rule and
   * joined the word already there, so the reference is named and quantified in
   * one place, on itself: "grensen 0,50".
   *
   * What is left in the margin is one figure at the end of the line, which is
   * the sparkline's own end-label and reads as a label on the data. */

  const sisteAv = (vs: (number | null)[]): number | null => {
    for (let i = Math.min(n, naa) - 1; i >= 0; i--) {
      const v = vs[i] ?? null
      if (v !== null && v !== undefined) return v
    }
    return null
  }
  const sisteSum = sisteAv(sitev.map((v, i) => (kol[i]!.fram ? null : v)))
  if (sisteSum !== null) {
    const g = naa > 0 ? (grense[naa - 1] ?? null) : null
    tall.push(<text key="tsum" className="syk-siste sum"
      x={W - TALL + 7} y={SY + SUM / 2 + 5}>
      {nb(sisteSum)}
      {g !== null && sisteSum >= g && <tspan className="over">{' !'}</tspan>}
    </text>)
  }
  if (merder) {
    rader.forEach((rad, r) => {
      const v = sisteAv(kol.map((k) => (k.fram || !k.dag ? null
        : k.dag.cells[rad.pen_id]?.adult_female ?? null)))
      if (v === null) return
      tall.push(<text key={`t${rad.pen_id}`} className="syk-siste"
        x={W - TALL + 7} y={y(r) + LANE / 2 + 4}>{nb(v)}</text>)
    })
  }

  /* ------------------------------------------------------------- scrubbing */

  const kolVed = (klientX: number): number | null => {
    const el = boks.current
    if (!el) return null
    const r = el.getBoundingClientRect()
    /* The SVG is laid out to fit the box, so a client pixel is not a viewBox
       pixel whenever the two differ -- which they do at every width where the
       right-hand gutter has been added. Scaling the offset first is what keeps
       the playhead under the finger instead of drifting right across it. */
    const skala2 = W / Math.max(1, r.width)
    const i = Math.floor(((klientX - r.left) * skala2 - gutterN) / cw)
    return i >= 0 && i < n ? i : null
  }

  /** Pointing reads; only a week that HAPPENED commits.
   *
   *  The playhead stops at now because the map cannot honour a week that has
   *  not happened — there are no counts in it and no occupancy either. A
   *  forecast column still answers under the finger, in the header, which is
   *  where its number belongs anyway: it is a figure for the locality and the
   *  map is a picture of merder. */
  const dra = (e: PekerHending, fest: boolean) => {
    const i = kolVed(e.clientX)
    if (i === null) return
    setPeker(i)
    if (fest && !kol[i]!.fram) onVelg(kol[i]!.dato)
  }

  /* THE DIAGONALS. The only mark here that is not about a week or a ring — it
     is about the seam between two series, and it crosses the lane boundary
     because that is exactly what it does to the data. */
  const flytt: ReactNode[] = []
  for (const m of merder ? moves : []) {
    if (!m.from_pen_id) continue
    const i = indeks.get(ukeNøkkel(m.on))
    const a = lane.get(m.from_pen_id), b = lane.get(m.pen_id)
    if (i === undefined || a === undefined || b === undefined || a === b) continue
    const cx = xm(i)
    const spenn = Math.max(9, Math.min(18, cw * 1.6))
    const x0 = cx - spenn / 2, x1 = cx + spenn / 2
    const ya = y(a) + LANE / 2, yb = y(b) + LANE / 2
    const ned = yb > ya
    flytt.push(
      <g key={m.occupancy_id} className="syk-flytt">
        <path d={`M${x0},${ya} C${cx},${ya} ${cx},${yb} ${x1},${yb}`} />
        {/* Pointing the way the fish went. Three points and no marker element:
            a marker inherits stroke rather than fill in every browser that has
            ever shipped one. */}
        <polygon points={`${x1},${yb} ${x1 - 4},${yb + (ned ? -4 : 4)} `
          + `${x1 + 4},${yb + (ned ? -4 : 4)}`} />
        <title>{`${m.group_name} · ${m.on}`}</title>
      </g>)
  }

  /* THE GENERATION, ALONG THE TOP: the day fish went in the water to the day
     the last of them left. One hairline rule, and its END is the whole point
     -- a rule that stops short of the right-hand edge is a farm with a harvest
     date, and one that runs off the edge is a farm still standing.

     ONE RULE FOR THE ARRAY, NOT ONE PER GROUP, AND NO NAMES.

     A group name is an identifier the crew uses when they are talking about
     fish, and there is nowhere on this band it can be read against anything.
     Worse: groups overlap by construction -- a split mints a second group over
     the same weeks as the first -- so one bar per group drew two rectangles on
     top of each other and two labels at the same x, and Ljosoy rendered "H25"
     underneath "H25-AC" as a single smear. Two bars that cannot be told apart
     are not two facts.

     So the bars are unioned into the one statement that survives having no
     label: THERE WERE FISH IN THIS ARRAY, FROM HERE TO HERE. Which group they
     belonged to is a question the panel above answers in a sentence, and the
     moves rail below answers ring by ring. */
  const gen: ReactNode[] = []
  {
    let fra: number | null = null, til: number | null = null
    for (const grp of groups) {
      /* No stocking date: the fish predate the window, so the rule starts at
         its left edge. No harvest date: they are still there, so it runs to
         the right edge -- through the forecast, which is exactly the claim the
         forecast is making. */
      const a = grp.stocked_on ? nærmeste(kol, grp.stocked_on) : 0
      const b = grp.harvested_on ? nærmeste(kol, grp.harvested_on) : n - 1
      if (a === null || b === null || b < a) continue
      fra = fra === null ? a : Math.min(fra, a)
      til = til === null ? b : Math.max(til, b)
    }
    if (fra !== null && til !== null) {
      const bx = x(fra), bw = Math.max(cw, x(til) + cw - bx)
      gen.push(<rect key="gen" className="syk-gen"
        x={bx} y={0} width={bw} height={3} rx={1.5} />)
    }
  }

  /* THE AXIS, AND THE THING IT NEVER SAID.
     Twenty pixels were reserved here for a week axis and all that was ever
     drawn in them was the year, once, in the corner -- plus bare tick lines at
     each month with no word on them. So the figure answered "how much" at
     every column and "when" at none of them, and a farmer looking at the
     spike could not say whether it was last month or in the spring.

     Months get their names. Not weeks: a week number is what the header prints
     for the week under the finger, and thirty of them along the bottom is the
     table this replaced. A month is the unit a delousing is planned in. */
  /* AND NO RULED LINES UP THE FIGURE.
     The last round gave the year and month ticks a stroke, having found they
     had never had one -- and that turned out to be the wrong half of the fix.
     A month word on the baseline locates time completely; a vertical line
     through the data as well divides the plot into panels, which is the one
     visual habit that makes a chart read as a monitoring dashboard rather than
     as a figure. Tufte's oldest rule and the cheapest to obey: the gridlines
     go, the words stay. */
  const merker: ReactNode[] = []
  let sisteÅr = '', sisteMnd = ''
  const romForNavn = cw * 4 >= 26   // a month is about four and a third weeks
  kol.forEach((k, i) => {
    const år = k.dato.slice(0, 4)
    const mnd = k.dato.slice(0, 7)
    if (år !== sisteÅr) {
      sisteÅr = år; sisteMnd = mnd
      merker.push(<text key={`å${i}`} className="syk-år-navn"
        x={x(i) + 2} y={H - AKSE + 12}>{år}</text>)
    } else if (mnd !== sisteMnd && romForNavn) {
      sisteMnd = mnd
      merker.push(<text key={`mn${i}`} className="syk-mnd-navn"
        x={x(i) + 2} y={H - AKSE + 12}>{månedKort(k.dato)}</text>)
    }
  })

  return (
    <div className="syklus" ref={boks}>
      {tittel}

      <svg className="syklus-flate" width="100%" height={H}
        viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMinYMin meet"
        role="slider" tabIndex={0}
        aria-label={t('The cycle')}
        aria-valuemin={0} aria-valuemax={Math.max(0, naa - 1)}
        aria-valuenow={Math.min(valgtI, Math.max(0, naa - 1))}
        aria-valuetext={vistKol
          ? `${t('Week')} ${isoUke(vistKol.dato)}` : ''}
        onPointerDown={(e) => {
          (e.target as Element).setPointerCapture?.(e.pointerId)
          dra(e, true) }}
        onPointerMove={(e) => dra(e, e.buttons === 1)}
        onPointerUp={() => setPeker(null)}
        onPointerLeave={() => setPeker(null)}
        onPointerCancel={() => setPeker(null)}
        onKeyDown={(e) => {
          const step = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1
            : e.key === 'Home' ? -n : e.key === 'End' ? n : 0
          if (!step) return
          e.preventDefault()
          const i = Math.max(0, Math.min(naa - 1, valgtI + step))
          if (kol[i]) onVelg(kol[i]!.dato)
        }}>
        {/* The weeks ahead sit on their own ground. Not a border and not a
            different palette — enough that the eye knows which half of the
            picture has already happened before it reads a number off it. */}
        {naa < n && (
          <rect className="syk-framgrunn" x={x(naa)} y={0}
            width={W - TALL - x(naa)} height={H - AKSE + 4} />
        )}
        {gen}
        {/* The axis stays put. The DATA draws in. */}
        {merker}

        {/* THE ONE PIECE OF MOTION IN THE FIGURE, and it is worth arguing for
            because `docs/principles.md` makes anything that moves justify
            itself as Function, Feeling or Decoration.

            It is Function first. A cycle is read LEFT TO RIGHT, oldest week to
            the weeks ahead, and nothing on a static figure says where to
            start; a reader's eye lands wherever the highest contrast is, which
            here is the peak in August. A sweep puts the eye at the beginning
            and walks it along time, once, in under a second — the same job an
            opening paragraph does. The number in the right-hand gutter arrives
            last, which is where the reading ends anyway.

            It runs ONCE, on mount, and never again. A figure that re-animates
            on every state change is a figure you cannot read while it is
            working, and a loop is the thing the eye learns to ignore. */}
        <defs>
          <clipPath id="syk-sveip-klipp">
            <rect className="syk-sveip" x={0} y={0} width={W} height={H} />
          </clipPath>
        </defs>
        <g clipPath="url(#syk-sveip-klipp)">
          {marker}
          {flytt}
          {tall}
        </g>

        {/* NOW. The line the whole thing is read either side of. */}
        {naa > 0 && naa < n && (
          <g className="syk-naa">
            <line x1={x(naa)} x2={x(naa)} y1={0} y2={H - AKSE + 12} />
            {/* Clear of the generation rule above it: the rule stops at 3
                and a 9px cap-height hangs about 6 above its baseline. */}
            <text x={x(naa) + 4} y={11}>{t('now')}</text>
          </g>
        )}

        {/* The merd numbers, painted over the gutter so a wide ribbon cannot
            scroll them away from the lane they name. Only when the merder are
            open: a gutter labelling one band is a column of nothing. */}
        {merder && (
          <>
            <rect className="syk-gutter" x={0} y={GEN + SUM} width={GUTTER}
              height={H - GEN - SUM} />
            {rader.map((r, i) => (
              <text key={r.pen_id}
                className={`syk-navn${r.retired ? ' ute' : ''}`}
                x={GUTTER - 8} y={y(i) + LANE / 2 + 4} textAnchor="end">
                {r.pen_no}
              </text>
            ))}
          </>
        )}

        {/* The playhead. The map above is showing this week. */}
        <g className="syk-hode">
          <line x1={xm(vist)} x2={xm(vist)} y1={0} y2={H - AKSE + 4} />
          <circle cx={xm(vist)} cy={3} r={3} />
        </g>
      </svg>

    </div>
  )
}

/** How far back the near window reaches: the start of the run of weeks that
 *  ends at now, cut to what the width can draw legibly.
 *
 *  LES is the narrowest a week may be drawn and still be a week -- a column
 *  you can put a finger on, and wide enough for the month underneath it to get
 *  its name. Below that the ribbon stops being a figure and becomes texture,
 *  which is what the old six-year view looked like and why it was not the
 *  default. A phone therefore shows fewer weeks than a monitor at the same
 *  column width, which is the right way round: the week is the constant and
 *  the span is what gives.
 *
 *  BRUDD is three weeks. A farm that misses a count over Easter has not
 *  stopped farming, and breaking the run there would throw away the cycle for
 *  a fortnight's silence; a gap longer than that is the register's coverage
 *  ending, and drawing across it would be a lie about time. */
function nærvindu(
  modell: { kol: Kol[]; naa: number }, bredde: number, merder: boolean,
): number {
  const LES = 16
  const BRUDD = 21 * 864e5
  const { kol, naa } = modell
  if (naa <= 0) return 0

  let i = naa - 1
  while (i > 0) {
    const her = kol[i]!.dato, før = kol[i - 1]!.dato
    if (Date.parse(her) - Date.parse(før) > BRUDD) break
    i--
  }

  /* How many columns fit at a legible width, forecast included -- the weeks
     ahead take the same room as the weeks behind and must be counted. */
  const plass = bredde - (merder ? GUTTER : 8) - TALL - 6
  const passer = Math.max(NAER, Math.floor(plass / LES) - (kol.length - naa))

  // At least NAER, never more than the run and never more than fits.
  return Math.max(0, Math.max(i, naa - passer), naa - MAKSUKER)
}

/** The month, in three or four letters, for the bottom of the figure.
 *
 *  Intl and not a table of our own: nb-NO already knows that August shortens
 *  to "aug." and May does not shorten at all. The trailing point goes -- it is
 *  a full stop under a chart, and at this size it reads as dirt on the axis. */
function månedKort(iso: string): string {
  return new Date(iso + 'T00:00:00Z')
    .toLocaleDateString('nb-NO', { month: 'short', timeZone: 'UTC' })
    .replace('.', '')
}

/** A key both a count date, a move date and a forecast week can be looked up
 *  by. Counts land on the day somebody went out; a move lands on the day the
 *  net was opened; the model answers on a Monday. They are one column when
 *  they are one week.
 *
 *  isoAar and not the calendar year: 2027-01-01 is ISO 2026-W53, and a key
 *  built from the calendar year files that day a year from the week it belongs
 *  to. The week is padded so the key sorts as well as compares. */
function ukeNøkkel(iso: string): string {
  return `${isoAar(iso)}-${String(isoUke(iso)).padStart(2, '0')}`
}

/** The column a date falls in, or the nearest one before it. For the
 *  generation bar, whose ends are stocking and harvest dates and need not be
 *  weeks anybody counted in. */
function nærmeste(kol: Kol[], iso: string): number | null {
  if (kol.length === 0) return null
  let best = 0
  for (let i = 0; i < kol.length; i++) {
    if (kol[i]!.dato <= iso) best = i
    else break
  }
  return best
}
